import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Lock,
  Unlock,
  Ban,
  CheckCircle,
  XCircle,
  Shield,
  Mail,
  Calendar,
  MapPin,
  AlertTriangle,
  User as UserIcon,
  RefreshCw,
  Key,
  Copy,
  X as CloseIcon
} from 'lucide-react';
import { userApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { isAdmin } from '../utils/roles';
import { homeFor } from '../utils/corridors';
import {
  formatDate,
  formatRelativeTime
} from '../utils/helpers';

// Password Reset Modal Component
const PasswordResetModal = ({ isOpen, onClose, username, temporaryPassword }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(temporaryPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = temporaryPassword;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-success-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-success-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Password Reset Successful</h3>
                <p className="text-sm text-gray-600">User: {username}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            {/* Warning Message */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-orange-800">
                  <p className="font-semibold mb-1">Important Security Notice</p>
                  <p>The user must change this password on their next login. Share this password securely and do not send it via email or unsecured channels.</p>
                </div>
              </div>
            </div>

            {/* Password Display */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Temporary Password
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={temporaryPassword}
                  readOnly
                  className="w-full px-4 py-3 pr-20 border-2 border-primary-300 rounded-lg bg-primary-50 text-primary-900 font-mono text-lg font-semibold focus:outline-none focus:ring-2 focus:ring-primary-500 select-all"
                  onClick={(e) => e.target.select()}
                />
                <button
                  onClick={handleCopy}
                  className={`absolute right-2 top-1/2 transform -translate-y-1/2 px-3 py-1.5 rounded-md transition-colors ${
                    copied
                      ? 'bg-success-600 text-white'
                      : 'bg-primary-600 text-white hover:bg-primary-700'
                  }`}
                >
                  <div className="flex items-center space-x-1.5">
                    {copied ? (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        <span className="text-xs font-medium">Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        <span className="text-xs font-medium">Copy</span>
                      </>
                    )}
                  </div>
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Click the password to select it, or use the Copy button
              </p>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Lock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-2">Next Steps:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Copy the temporary password using the button above</li>
                    <li>Share it securely with the user (phone call, WhatsApp)</li>
                    <li>User will be forced to change password on next login</li>
                    <li>Do not share via email or SMS for security reasons</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Password Reset Modal State
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetPassword, setResetPassword] = useState('');

  useEffect(() => {
    loadUser();
  }, [id]);

  const loadUser = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userApi.getUserById(id);
      setUser(data);
    } catch (err) {
      console.error('Failed to load user:', err);
      setError('User not found');
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async () => {
    if (!window.confirm('Are you sure you want to unlock this user account?')) {
      return;
    }

    try {
      await userApi.unlockUser(id);
      await loadUser();
    } catch (err) {
      console.error('Failed to unlock user:', err);
      alert('Failed to unlock user: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleActivate = async () => {
    try {
      await userApi.activateUser(id);
      await loadUser();
    } catch (err) {
      console.error('Failed to activate user:', err);
      alert('Failed to activate user: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) {
      return;
    }

    try {
      await userApi.deactivateUser(id);
      await loadUser();
    } catch (err) {
      console.error('Failed to deactivate user:', err);
      alert('Failed to deactivate user: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleResetPassword = async () => {
    if (!window.confirm(`Reset password for ${user.username}? They will need to change it on next login.`)) {
      return;
    }

    try {
      const response = await userApi.resetPassword(id);
      
      // Show modal with temporary password
      setResetPassword(response.temporaryPassword);
      setShowResetModal(true);
      
      // Reload user to show updated status
      await loadUser();
    } catch (err) {
      console.error('Failed to reset password:', err);
      alert('Failed to reset password: ' + (err.response?.data?.error || err.message));
    }
  };

  const getRoleBadgeClass = (role) => {
    const classes = {
      ADMIN: 'badge bg-purple-100 text-purple-800 border-purple-200',
      OPERATOR: 'badge bg-blue-100 text-blue-800 border-blue-200',
      VIEWER: 'badge bg-gray-100 text-gray-800 border-gray-200'
    };
    return classes[role] || 'badge-info';
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      ACTIVE: 'badge-success',
      INACTIVE: 'badge-secondary',
      LOCKED: 'badge-danger',
      PENDING: 'badge-warning'
    };
    return classes[status] || 'badge-info';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ACTIVE':
        return <CheckCircle className="w-5 h-5 text-success-600" />;
      case 'INACTIVE':
        return <Ban className="w-5 h-5 text-gray-600" />;
      case 'LOCKED':
        return <Lock className="w-5 h-5 text-danger-600" />;
      case 'PENDING':
        return <AlertTriangle className="w-5 h-5 text-warning-600" />;
      default:
        return <XCircle className="w-5 h-5 text-gray-600" />;
    }
  };

  const isOwnProfile = currentUser?.id === parseInt(id);
  // Managing users is for admins, as the engine enforces; everyone else sees their
  // own page only, and Back takes them home rather than to the admin-only list.
  const admin = isAdmin(currentUser);
  const backTo = admin ? '/users' : homeFor(currentUser);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
          <p className="text-gray-600">Loading user...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
          <p className="text-gray-900 font-medium mb-2">Error Loading User</p>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={() => navigate(backTo)} className="btn-primary">
            {admin ? 'Back to Users' : 'Back'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Password Reset Modal */}
      <PasswordResetModal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        username={user.username}
        temporaryPassword={resetPassword}
      />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(backTo)}
              className="btn-secondary flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{user.fullName}</h1>
              <p className="text-gray-600 mt-1">@{user.username}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {isOwnProfile && (
              <button
                onClick={() => navigate('/change-password')}
                className="btn-secondary flex items-center space-x-2 bg-blue-50 text-blue-700 hover:bg-blue-100"
              >
                <Key className="w-4 h-4" />
                <span>Change Password</span>
              </button>
            )}
            
            {admin && user.status === 'LOCKED' && (
              <button
                onClick={handleUnlock}
                className="btn-secondary flex items-center space-x-2 bg-success-50 text-success-700 hover:bg-success-100"
              >
                <Unlock className="w-4 h-4" />
                <span>Unlock</span>
              </button>
            )}
            {admin && user.status === 'ACTIVE' && !isOwnProfile && (
              <button
                onClick={handleDeactivate}
                className="btn-secondary flex items-center space-x-2 bg-warning-50 text-warning-700 hover:bg-warning-100"
              >
                <Ban className="w-4 h-4" />
                <span>Deactivate</span>
              </button>
            )}
            {admin && user.status === 'INACTIVE' && (
              <button
                onClick={handleActivate}
                className="btn-secondary flex items-center space-x-2 bg-success-50 text-success-700 hover:bg-success-100"
              >
                <CheckCircle className="w-4 h-4" />
                <span>Activate</span>
              </button>
            )}
            {!isOwnProfile && admin && (
              <button
                onClick={handleResetPassword}
                className="btn-secondary flex items-center space-x-2"
              >
                <Lock className="w-4 h-4" />
                <span>Reset Password</span>
              </button>
            )}
            {admin && (
              <button
                onClick={() => navigate(`/users/${id}/edit`)}
                className="btn-primary flex items-center space-x-2"
              >
                <Edit className="w-4 h-4" />
                <span>Edit</span>
              </button>
            )}
          </div>
        </div>

        {/* Rest of the component remains the same... */}
        {/* User Profile Card */}
        <div className="card">
          <div className="card-body">
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0">
                <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center">
                  <span className="text-primary-700 font-bold text-3xl">
                    {user.fullName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <UserIcon className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="text-xs text-gray-500">Username</div>
                    <div className="text-sm font-medium text-gray-900">@{user.username}</div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="text-xs text-gray-500">Email</div>
                    <div className="text-sm font-medium text-gray-900">{user.email}</div>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Shield className="w-5 h-5 text-gray-400" />
                  <div>
                    <div className="text-xs text-gray-500">Role</div>
                    <span className={getRoleBadgeClass(user.role)}>{user.role}</span>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  {getStatusIcon(user.status)}
                  <div>
                    <div className="text-xs text-gray-500">Status</div>
                    <span className={getStatusBadgeClass(user.status)}>{user.status}</span>
                  </div>
                </div>
              </div>
            </div>

            {(user.mustChangePassword || user.status === 'LOCKED') && (
              <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-2">
                {user.mustChangePassword && (
                  <span className="badge bg-orange-100 text-orange-800 border-orange-200">
                    Password Reset Required
                  </span>
                )}
                {user.status === 'LOCKED' && user.lockedUntil && (
                  <span className="badge bg-red-100 text-red-800 border-red-200">
                    Locked until {formatDate(user.lockedUntil, 'PPp')}
                  </span>
                )}
              </div>
            )}

            {isOwnProfile && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <UserIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">This is your profile</p>
                      <p className="text-xs">
                        {admin
                          ? 'You can update your information by clicking the "Edit" button or change your password using the "Change Password" button above.'
                          : 'Change your password with the "Change Password" button above. To change your name or email, ask an admin at your institution.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Activity Information - Keep existing code */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Activity Information</h2>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Last Login</h3>
                {user.lastLoginAt ? (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">
                        {formatDate(user.lastLoginAt, 'PPp')}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      ({formatRelativeTime(user.lastLoginAt)})
                    </div>
                    {user.lastLoginIp && (
                      <div className="flex items-center space-x-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{user.lastLoginIp}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Never logged in</p>
                )}
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Account Created</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-900">
                      {formatDate(user.createdAt, 'PPp')}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    ({formatRelativeTime(user.createdAt)})
                  </div>
                  {user.createdBy && (
                    <div className="text-sm text-gray-600">by {user.createdBy}</div>
                  )}
                </div>
              </div>

              {user.failedLoginAttempts > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Security Alerts</h3>
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="w-4 h-4 text-warning-600" />
                    <span className="text-sm text-gray-900">
                      {user.failedLoginAttempts} failed login attempt(s)
                    </span>
                  </div>
                </div>
              )}

              {user.updatedAt && user.updatedAt !== user.createdAt && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Last Updated</h3>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">
                        {formatDate(user.updatedAt, 'PPp')}
                      </span>
                    </div>
                    {user.updatedBy && (
                      <div className="text-sm text-gray-600">by {user.updatedBy}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Notes */}
        {user.notes && (
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-gray-900">Notes</h2>
            </div>
            <div className="card-body">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{user.notes}</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default UserDetail;
